// src/components/AccountPickerSingle.tsx
import React, { useState } from 'react';
import { Box, Typography, TextField, MenuItem } from '@mui/material';

export interface AccountOption {
  id: string;
  name: string;
  phone_number?: string;
}

interface Props {
  accounts: AccountOption[];
  selected: string;
  onChange: (name: string) => void;
  placeholder?: string;
}

const AccountPickerSingle: React.FC<Props> = ({
  accounts,
  selected,
  onChange,
  placeholder = 'Выберите аккаунт',
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = accounts.filter(acc =>
    acc.name.toLowerCase().includes(search.toLowerCase()) ||
    acc.phone_number?.includes(search)
  ).slice(0, 50);

  const totalFiltered = accounts.filter(acc =>
    acc.name.toLowerCase().includes(search.toLowerCase()) ||
    acc.phone_number?.includes(search)
  ).length;

  const select = (name: string) => {
    onChange(name);
    setOpen(false);
    setSearch('');
  };

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{
          width: '100%',
          border: open ? '2px solid #1E40AF' : '1px solid #C4C4C4',
          borderRadius: 1, px: 1.5, py: 1, minHeight: 56, cursor: 'pointer',
          display: 'flex', alignItems: 'center', boxSizing: 'border-box', bgcolor: 'white',
          '&:hover': { borderColor: '#1E40AF' },
        }}
      >
        {selected
          ? <Typography sx={{ color: '#1E40AF', fontWeight: 500 }}>{selected}</Typography>
          : <Typography color="text.secondary">{placeholder}</Typography>
        }
      </Box>

      {open && (
        <>
          <Box
            onClick={() => { setOpen(false); setSearch(''); }}
            sx={{ position: 'fixed', inset: 0, zIndex: 1200 }}
          />
          <Box sx={{
            position: 'absolute', top: '100%', left: 0, width: '100%', zIndex: 1300,
            bgcolor: 'white', border: '1px solid #E5E7EB', borderRadius: 1,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)', mt: 0.5,
            display: 'flex', flexDirection: 'column', maxHeight: 400,
          }}>
            <Box sx={{ p: 1.5, borderBottom: '1px solid #E5E7EB' }}>
              <TextField
                autoFocus size="small" fullWidth
                placeholder="Поиск по имени или номеру..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onClick={e => e.stopPropagation()}
              />
            </Box>
            <Box sx={{ overflowY: 'auto', flex: 1 }}>
              {filtered.length === 0
                ? <Typography sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>
                    Ничего не найдено
                  </Typography>
                : filtered.map(acc => (
                  <MenuItem
                    key={acc.id} dense
                    onClick={e => { e.stopPropagation(); select(acc.name); }}
                    selected={selected === acc.name}
                    sx={{ py: 0.75, px: 1.5 }}
                  >
                    <Box sx={{ overflow: 'hidden' }}>
                      <Typography variant="body2" fontWeight={selected === acc.name ? 600 : 400} noWrap>
                        {acc.name}
                      </Typography>
                      {acc.phone_number && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {acc.phone_number}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))
              }
              {totalFiltered > 50 && (
                <Typography sx={{ p: 1.5, fontSize: '0.75rem', color: '#6B7280', textAlign: 'center' }}>
                  Показаны первые 50 — уточните поиск
                </Typography>
              )}
            </Box>
            <Box sx={{ px: 2, py: 1, borderTop: '1px solid #E5E7EB', bgcolor: '#F9FAFB' }}>
              <Typography variant="caption" color="text.secondary">
                {search ? `Найдено: ${totalFiltered}` : `Всего: ${accounts.length}`}
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default AccountPickerSingle;